import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { formSchemas } from '../../../utils/validation';

const RegistrationForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    city: '',
    state: '',
    courseInterest: '',
    examStatus: '',
    budgetRange: '',
    studyMode: '',
    dataConsent: false,
    communicationConsent: false
  });

  const [errors, setErrors] = useState({});
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({});

  const stateOptions = [
    { value: 'AN', label: 'Andaman and Nicobar Islands' },
    { value: 'AP', label: 'Andhra Pradesh' },
    { value: 'AR', label: 'Arunachal Pradesh' },
    { value: 'AS', label: 'Assam' },
    { value: 'BR', label: 'Bihar' },
    { value: 'CH', label: 'Chandigarh' },
    { value: 'CT', label: 'Chhattisgarh' },
    { value: 'DN', label: 'Dadra and Nagar Haveli and Daman and Diu' },
    { value: 'DL', label: 'Delhi' },
    { value: 'GA', label: 'Goa' },
    { value: 'GJ', label: 'Gujarat' },
    { value: 'HR', label: 'Haryana' },
    { value: 'HP', label: 'Himachal Pradesh' },
    { value: 'JK', label: 'Jammu and Kashmir' },
    { value: 'JH', label: 'Jharkhand' },
    { value: 'KA', label: 'Karnataka' },
    { value: 'KL', label: 'Kerala' },
    { value: 'LA', label: 'Ladakh' },
    { value: 'LD', label: 'Lakshadweep' },
    { value: 'MP', label: 'Madhya Pradesh' },
    { value: 'MH', label: 'Maharashtra' },
    { value: 'MN', label: 'Manipur' },
    { value: 'ML', label: 'Meghalaya' },
    { value: 'MZ', label: 'Mizoram' },
    { value: 'NL', label: 'Nagaland' },
    { value: 'OR', label: 'Odisha' },
    { value: 'PY', label: 'Puducherry' },
    { value: 'PB', label: 'Punjab' },
    { value: 'RJ', label: 'Rajasthan' },
    { value: 'SK', label: 'Sikkim' },
    { value: 'TN', label: 'Tamil Nadu' },
    { value: 'TG', label: 'Telangana' },
    { value: 'TR', label: 'Tripura' },
    { value: 'UP', label: 'Uttar Pradesh' },
    { value: 'UT', label: 'Uttarakhand' },
    { value: 'WB', label: 'West Bengal' }
  ];

  const courseOptions = [
    { value: 'engineering', label: 'Engineering' },
    { value: 'medicine', label: 'Medicine' },
    { value: 'business', label: 'Business Administration' },
    { value: 'arts', label: 'Arts & Humanities' },
    { value: 'science', label: 'Science' },
    { value: 'law', label: 'Law' },
    { value: 'design', label: 'Design' },
    { value: 'computer-science', label: 'Computer Science' },
    { value: 'nursing', label: 'Nursing' },
    { value: 'pharmacy', label: 'Pharmacy' }
  ];

  const examStatusOptions = [
    { value: 'not-appeared', label: 'Not Appeared Yet' },
    { value: 'appeared', label: 'Appeared - Awaiting Results' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'not-qualified', label: 'Not Qualified' }
  ];

  const budgetOptions = [
    { value: 'below-1lakh', label: 'Below ₹1 Lakh/year' },
    { value: '1lakh-3lakh', label: '₹1 Lakh - ₹3 Lakh/year' },
    { value: '3lakh-5lakh', label: '₹3 Lakh - ₹5 Lakh/year' },
    { value: '5lakh-10lakh', label: '₹5 Lakh - ₹10 Lakh/year' },
    { value: 'above-10lakh', label: 'Above ₹10 Lakh/year' }
  ];

  const studyModeOptions = [
    { value: 'full-time', label: 'Full-time On-campus' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'online', label: 'Online/Distance Learning' },
    { value: 'hybrid', label: 'Hybrid (Online + On-campus)' }
  ];

  const validateField = (name, value, currentFormData = formData) => {
    // Use validation schemas
    if (formSchemas?.registration?.[name]) {
      const result = formSchemas?.registration?.[name](value);
      return result?.error || '';
    }
    
    switch (name) {
      case 'confirmPassword':
        if (!value?.trim()) return 'Please confirm your password';
        if (value !== currentFormData?.password) return 'Passwords do not match';
        return '';
      case 'city':
        if (!value?.trim()) return 'City is required';
        if (value?.trim()?.length < 2) return 'City name must be at least 2 characters';
        return '';
      case 'state':
        if (!value) return 'State is required';
        return '';
      case 'courseInterest':
        if (!value) return 'Course interest is required';
        return '';
      case 'dataConsent':
        if (!value) return 'You must agree to data processing';
        return '';
      case 'communicationConsent':
        if (!value) return 'You must agree to receive communications';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (name, value) => {
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    
    // Only validate if field has been touched
    if (touched?.[name]) {
      const error = validateField(name, value, updatedFormData);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
    
    // If password changed, re-validate confirmPassword if it's been touched
    if (name === 'password' && touched?.confirmPassword && updatedFormData?.confirmPassword) {
      const confirmError = validateField('confirmPassword', updatedFormData?.confirmPassword, updatedFormData);
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData?.[name], formData);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Mark all fields as touched
    const allFields = ['fullName', 'email', 'mobile', 'password', 'confirmPassword', 'city', 'state', 'courseInterest', 'dataConsent', 'communicationConsent'];
    const touchedFields = {};
    allFields?.forEach(field => touchedFields[field] = true);
    setTouched(touchedFields);
    
    const newErrors = {};
    const requiredFields = ['fullName', 'email', 'mobile', 'password', 'confirmPassword', 'city', 'state', 'courseInterest', 'dataConsent', 'communicationConsent'];
    
    requiredFields?.forEach(field => {
      const error = validateField(field, formData?.[field], formData);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)?.[0];
      const element = document.getElementById(`input-${firstErrorField}`);
      if (element) {
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element?.focus();
      }
      return;
    }

    // Remove confirmPassword before submitting (not needed on backend)
    const { confirmPassword, ...submitData } = formData;
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <div className="space-y-4">
        <h3 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="User" size={20} color="var(--color-primary)" />
          Personal Information
        </h3>

        <Input
          id="input-fullName"
          label="Full Name"
          type="text"
          placeholder="Enter your full name"
          value={formData?.fullName}
          onChange={(e) => handleChange('fullName', e?.target?.value)}
          onBlur={() => handleBlur('fullName')}
          error={touched?.fullName ? errors?.fullName : ''}
          required
          disabled={isLoading}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            id="input-email"
            label="Email Address"
            type="email"
            placeholder="your.email@example.com"
            description="We'll send OTP to this email"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            onBlur={() => handleBlur('email')}
            error={touched?.email ? errors?.email : ''}
            required
            disabled={isLoading}
          />

          <Input
            id="input-mobile"
            label="Mobile Number"
            type="tel"
            placeholder="9876543210"
            description="10-digit Indian mobile number"
            value={formData?.mobile}
            onChange={(e) => handleChange('mobile', e?.target?.value)}
            onBlur={() => handleBlur('mobile')}
            error={touched?.mobile ? errors?.mobile : ''}
            required
            disabled={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Input
              id="input-password"
              label="Password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              description="Min 8 characters with uppercase, lowercase, and number"
              value={formData?.password}
              onChange={(e) => handleChange('password', e?.target?.value)}
              onBlur={() => handleBlur('password')}
              error={touched?.password ? errors?.password : ''}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isLoading}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>

          <div className="relative">
            <Input
              id="input-confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={formData?.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
              onBlur={() => handleBlur('confirmPassword')}
              error={touched?.confirmPassword ? errors?.confirmPassword : ''}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isLoading}
            >
              <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="City"
            type="text"
            placeholder="Enter your city"
            value={formData?.city}
            onChange={(e) => handleChange('city', e?.target?.value)}
            error={errors?.city}
            required
            disabled={isLoading}
          />

          <Select
            label="State"
            placeholder="Select your state"
            options={stateOptions}
            value={formData?.state}
            onChange={(value) => handleChange('state', value)}
            error={errors?.state}
            required
            searchable
            disabled={isLoading}
          />
        </div>

        <Select
          label="Course Interest"
          placeholder="Select your area of interest"
          description="Choose the field you want to pursue"
          options={courseOptions}
          value={formData?.courseInterest}
          onChange={(value) => handleChange('courseInterest', value)}
          error={errors?.courseInterest}
          required
          searchable
          disabled={isLoading}
        />
      </div>
      <div className="border-t border-border pt-6">
        <button
          type="button"
          onClick={() => setShowOptionalFields(!showOptionalFields)}
          className="flex items-center gap-2 text-primary hover:text-primary/80 transition-smooth mb-4"
        >
          <Icon 
            name={showOptionalFields ? 'ChevronUp' : 'ChevronDown'} 
            size={20} 
          />
          <span className="font-medium">
            {showOptionalFields ? 'Hide' : 'Show'} Optional Information
          </span>
        </button>

        {showOptionalFields && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <Select
              label="Exam Status"
              placeholder="Select your exam status"
              description="Optional: Your entrance exam status"
              options={examStatusOptions}
              value={formData?.examStatus}
              onChange={(value) => handleChange('examStatus', value)}
              disabled={isLoading}
            />

            <Select
              label="Budget Range"
              placeholder="Select your budget range"
              description="Optional: Annual tuition budget"
              options={budgetOptions}
              value={formData?.budgetRange}
              onChange={(value) => handleChange('budgetRange', value)}
              disabled={isLoading}
            />

            <Select
              label="Preferred Study Mode"
              placeholder="Select your preferred study mode"
              description="Optional: How you prefer to study"
              options={studyModeOptions}
              value={formData?.studyMode}
              onChange={(value) => handleChange('studyMode', value)}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
      <div className="border-t border-border pt-6 space-y-4">
        <h3 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Shield" size={20} color="var(--color-primary)" />
          Consent & Privacy
        </h3>

        <Checkbox
          label="I agree to data processing"
          description="I consent to the collection and processing of my personal data for registration and participation in the virtual education fair"
          checked={formData?.dataConsent}
          onChange={(e) => handleChange('dataConsent', e?.target?.checked)}
          error={errors?.dataConsent}
          required
          disabled={isLoading}
        />

        <Checkbox
          label="I agree to receive communications"
          description="I consent to receive event updates, institute information, and promotional communications via email, WhatsApp, and SMS"
          checked={formData?.communicationConsent}
          onChange={(e) => handleChange('communicationConsent', e?.target?.checked)}
          error={errors?.communicationConsent}
          required
          disabled={isLoading}
        />

        <p className="text-sm text-muted-foreground">
          By registering, you agree to our{' '}
          <a href="#" className="text-primary hover:underline">Privacy Policy</a>
          {' '}and{' '}
          <a href="#" className="text-primary hover:underline">Terms of Service</a>
        </p>
      </div>
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="ArrowRight"
        iconPosition="right"
      >
        Continue to Verification
      </Button>
    </form>
  );
};

export default RegistrationForm;